import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const EMPTY = {
  agency_name: "",
  legal_name: "",
  country: "Portugal",
  region: "",
  district: "",
  city: "",
  address: "",
  website: "",
  licence_number: "",
  licence_jurisdiction: "",
  verification_status: "unverified",

  contact_name: "",
  job_title: "",
  email: "",
  phone: "",
  whatsapp: "",
  languages: [],

  coverage_countries: [],
  coverage_regions: [],
  coverage_cities: [],
  property_specialisations: [],

  relationship_stage: "discovered",
  cooperation_model: "case_by_case",
  commission_split_percent: "",
  fixed_referral_fee: "",
  currency: "EUR",
  agreement_status: "none",
  agreement_signed: false,
  protection_period_days: "",
  ncnd_confidentiality: false,

  source: "",
  notes: "",
};

function listToText(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

function textToList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function AgencyPartnerForm({
  open,
  partner,
  meta,
  saving,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;

    setForm({
      ...EMPTY,
      ...(partner || {}),
      commission_split_percent:
        partner?.commission_split_percent ?? "",
      fixed_referral_fee:
        partner?.fixed_referral_fee ?? "",
      protection_period_days:
        partner?.protection_period_days ?? "",
    });
  }, [open, partner]);

  if (!open) return null;

  const set = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();

    onSave({
      ...form,
      languages: form.languages || [],
      coverage_countries: form.coverage_countries || [],
      coverage_regions: form.coverage_regions || [],
      coverage_cities: form.coverage_cities || [],
      property_specialisations:
        form.property_specialisations || [],
    });
  };

  const specialisations =
    meta?.property_specialisations || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto my-6 max-w-5xl rounded-2xl bg-white shadow-2xl">
        <form onSubmit={submit}>
          <div className="border-b px-6 py-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t(
                "agencyPartners.form.eyebrow",
                "Agency Partner Network"
              )}
            </div>

            <h2 className="mt-1 text-2xl font-semibold text-slate-900">
              {partner
                ? t("agencyPartners.form.editTitle", "Edit agency partner")
                : t("agencyPartners.form.title", "Add agency partner")}
            </h2>
          </div>

          <div className="space-y-8 p-6">

            <section>
              <h3 className="mb-4 text-lg font-semibold">
                {t(
                  "agencyPartners.form.agencyInformation",
                  "Agency information"
                )}
              </h3>

              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label={t("agencyPartners.form.agencyName", "Agency name")}
                  required
                  value={form.agency_name}
                  onChange={(v) => set("agency_name", v)}
                />

                <Field
                  label={t("agencyPartners.form.legalName", "Legal company name")}
                  value={form.legal_name}
                  onChange={(v) => set("legal_name", v)}
                />

                <Field
                  label={t("agencyPartners.form.country", "Country")}
                  required
                  value={form.country}
                  onChange={(v) => set("country", v)}
                />

                <Field
                  label={t("agencyPartners.form.region", "Region")}
                  value={form.region}
                  onChange={(v) => set("region", v)}
                />

                <Field
                  label={t("agencyPartners.form.district", "District")}
                  value={form.district}
                  onChange={(v) => set("district", v)}
                />

                <Field
                  label={t("agencyPartners.form.city", "City")}
                  value={form.city}
                  onChange={(v) => set("city", v)}
                />

                <Field
                  label={t("agencyPartners.form.address", "Address")}
                  value={form.address}
                  onChange={(v) => set("address", v)}
                />

                <Field
                  label={t("agencyPartners.form.website", "Official website")}
                  type="url"
                  value={form.website}
                  onChange={(v) => set("website", v)}
                />

                <Field
                  label={t(
                    "agencyPartners.form.licenceNumber",
                    "AMI / licence number"
                  )}
                  value={form.licence_number}
                  onChange={(v) => set("licence_number", v)}
                />

                <Field
                  label={t(
                    "agencyPartners.form.licenceJurisdiction",
                    "Licence jurisdiction"
                  )}
                  value={form.licence_jurisdiction}
                  onChange={(v) => set("licence_jurisdiction", v)}
                />

                <SelectField
                  label={t(
                    "agencyPartners.form.verificationStatus",
                    "Verification status"
                  )}
                  value={form.verification_status}
                  onChange={(v) => set("verification_status", v)}
                  options={meta?.verification_statuses || []}
                />
              </div>
            </section>

            <section>
              <h3 className="mb-4 text-lg font-semibold">
                {t(
                  "agencyPartners.form.marketCoverage",
                  "Market coverage & specialisation"
                )}
              </h3>

              <div className="grid gap-4 md:grid-cols-2">
                <ListField
                  label={t(
                    "agencyPartners.form.coverageCountries",
                    "Countries covered"
                  )}
                  value={form.coverage_countries}
                  onChange={(v) => set("coverage_countries", v)}
                />

                <ListField
                  label={t(
                    "agencyPartners.form.coverageRegions",
                    "Regions / districts covered"
                  )}
                  value={form.coverage_regions}
                  onChange={(v) => set("coverage_regions", v)}
                />

                <ListField
                  label={t(
                    "agencyPartners.form.coverageCities",
                    "Cities covered"
                  )}
                  value={form.coverage_cities}
                  onChange={(v) => set("coverage_cities", v)}
                />

                <ListField
                  label={t("agencyPartners.form.languages", "Languages")}
                  value={form.languages}
                  onChange={(v) => set("languages", v)}
                />
              </div>

              <div className="mt-4">
                <div className="mb-2 text-sm font-medium">
                  {t(
                    "agencyPartners.form.specialisations",
                    "Property specialisations"
                  )}
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {specialisations.map((item) => {
                    const checked =
                      form.property_specialisations.includes(item);

                    return (
                      <label
                        key={item}
                        className="flex items-center gap-2 rounded-lg border p-3 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              set("property_specialisations", [
                                ...form.property_specialisations,
                                item,
                              ]);
                            } else {
                              set(
                                "property_specialisations",
                                form.property_specialisations.filter(
                                  (value) => value !== item
                                )
                              );
                            }
                          }}
                        />

                        {t(
                          `agencyPartners.specialisations.${item}`,
                          item.replaceAll("_", " ")
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-4 text-lg font-semibold">
                {t(
                  "agencyPartners.form.professionalContact",
                  "Professional contact"
                )}
              </h3>

              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label={t("agencyPartners.form.contactName", "Contact person")}
                  value={form.contact_name}
                  onChange={(v) => set("contact_name", v)}
                />

                <Field
                  label={t("agencyPartners.form.jobTitle", "Position")}
                  value={form.job_title}
                  onChange={(v) => set("job_title", v)}
                />

                <Field
                  label={t("agencyPartners.form.email", "Professional email")}
                  type="email"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                />

                <Field
                  label={t("agencyPartners.form.phone", "Telephone")}
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                />

                <Field
                  label={t("agencyPartners.form.whatsapp", "WhatsApp")}
                  value={form.whatsapp}
                  onChange={(v) => set("whatsapp", v)}
                />
              </div>
            </section>

            <section>
              <h3 className="mb-4 text-lg font-semibold">
                {t(
                  "agencyPartners.form.cooperation",
                  "Cooperation & commission"
                )}
              </h3>

              <div className="grid gap-4 md:grid-cols-2">
                <SelectField
                  label={t(
                    "agencyPartners.form.relationshipStage",
                    "Relationship stage"
                  )}
                  value={form.relationship_stage}
                  onChange={(v) => set("relationship_stage", v)}
                  options={meta?.relationship_stages || []}
                />

                <SelectField
                  label={t(
                    "agencyPartners.form.cooperationModel",
                    "Cooperation model"
                  )}
                  value={form.cooperation_model}
                  onChange={(v) => set("cooperation_model", v)}
                  options={meta?.cooperation_models || []}
                />

                <Field
                  label={t(
                    "agencyPartners.form.commissionSplit",
                    "Commission split (%)"
                  )}
                  type="number"
                  value={form.commission_split_percent}
                  onChange={(v) => set("commission_split_percent", v)}
                />

                <Field
                  label={t(
                    "agencyPartners.form.referralFee",
                    "Fixed referral fee"
                  )}
                  type="number"
                  value={form.fixed_referral_fee}
                  onChange={(v) => set("fixed_referral_fee", v)}
                />

                <Field
                  label={t("agencyPartners.form.currency", "Currency")}
                  value={form.currency}
                  onChange={(v) => set("currency", v)}
                />

                <SelectField
                  label={t(
                    "agencyPartners.form.agreementStatus",
                    "Agreement status"
                  )}
                  value={form.agreement_status}
                  onChange={(v) => set("agreement_status", v)}
                  options={meta?.agreement_statuses || []}
                />

                <Field
                  label={t(
                    "agencyPartners.form.protectionPeriod",
                    "Protection period (days)"
                  )}
                  type="number"
                  value={form.protection_period_days}
                  onChange={(v) => set("protection_period_days", v)}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-6">
                <CheckField
                  label={t(
                    "agencyPartners.form.agreementSigned",
                    "Agreement signed"
                  )}
                  checked={form.agreement_signed}
                  onChange={(v) => set("agreement_signed", v)}
                />

                <CheckField
                  label={t(
                    "agencyPartners.form.ncnd",
                    "NCND / confidentiality agreed"
                  )}
                  checked={form.ncnd_confidentiality}
                  onChange={(v) => set("ncnd_confidentiality", v)}
                />
              </div>
            </section>

            <section>
              <h3 className="mb-4 text-lg font-semibold">
                {t("agencyPartners.form.internal", "CRM information")}
              </h3>

              <div className="grid gap-4">
                <Field
                  label={t("agencyPartners.form.source", "Source")}
                  value={form.source}
                  onChange={(v) => set("source", v)}
                />

                <label className="block">
                  <span className="mb-1 block text-sm font-medium">
                    {t("agencyPartners.form.notes", "Internal notes")}
                  </span>

                  <textarea
                    rows={5}
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    className="w-full rounded-lg border px-3 py-2"
                  />
                </label>
              </div>
            </section>

          </div>

          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border px-5 py-2"
            >
              {t("common.cancel", "Cancel")}
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-slate-900 px-5 py-2 text-white disabled:opacity-50"
            >
              {saving
                ? t("agencyPartners.form.saving", "Saving…")
                : t("agencyPartners.form.save", "Save agency")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border px-3 py-2"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border px-3 py-2"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replaceAll("_", " ")}
          </option>
        ))}
      </select>
    </label>
  );
}

function ListField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <input
        value={listToText(value)}
        onChange={(e) => onChange(textToList(e.target.value))}
        placeholder="Portugal, Spain, France"
        className="w-full rounded-lg border px-3 py-2"
      />
      <span className="mt-1 block text-xs text-slate-500">
        Separate multiple values with commas.
      </span>
    </label>
  );
}

function CheckField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
